import { withOnLinkClickTracking } from 'auto-tracking';
import getYear from 'date-fns/getYear';
import {
  Box,
  Column,
  Columns,
  css,
  H6,
  P,
  rem,
  Spacer,
  styled,
  theme,
} from 'design-system';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { useEffect, useState } from 'react';

import cactusLogo from '../../public/cactus.png';
import { SectionInner } from '../components/section';
import { Journal } from '../resources/journal';

const FooterH6 = styled(H6, {
  fontWeight: theme.fontWeights.semiBold,
  fontFamily: 'inherit',
  fontSize: rem(14),
});
const FooterP = styled(P, {
  color: theme.colors.blue12,
  fontFamily: 'inherit',
  fontSize: rem(14),
});
const FooterA = withOnLinkClickTracking(
  styled('a', {
    color: 'inherit',
    textDecoration: 'none',

    '&:hover': {
      textDecoration: 'underline',
    },
  }),
);

export const FooterInnerSection = ({
  journal,
  onLogoClick,
}: {
  journal?: Journal;
  onLogoClick?: () => void;
}) => {
  const { t, i18n } = useTranslation(['common'], {
    bindI18n: 'languageChanged loaded',
  });

  const [showTooltip, setShowTooltip] = useState(false);

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  // bindI18n: loaded is needed because of the reloadResources call
  // if all pages use the reloadResources mechanism, the bindI18n option can also be defined in next-i18next.config.js
  useEffect(() => {
    i18n.reloadResources(i18n.resolvedLanguage, ['common']);
  }, []);

  return (
    <SectionInner
      css={{ color: theme.colors.gray5 }}
      aria-label="Footer Section"
    >
      <Spacer height={64} />
      <Columns layout="1-1-1-1" role="rowgroup">
        <Column
          css={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
          role="row"
        >
          <Box>
            {(journal?.publisher.slug === 'endnote' ||
              journal?.publisher.slug === 'refworks') && (
              <>
                {journal?.publisher.slug === 'endnote' ? (
                  <Image
                    src="/endnote.svg"
                    width={400}
                    height={100}
                    alt="Endnote Logo"
                    className={preflightJournalLogo()}
                  />
                ) : journal.publisher.slug === 'refworks' ? (
                  <ALink
                    href="https://refworks.proquest.com/learn-more/"
                    data-test-id="refworks-logo-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Image
                      src="/refworks.svg"
                      width={400}
                      height={100}
                      alt="refworks Logo"
                      className={preflightJournalLogo()}
                      onClick={onLogoClick}
                    />
                  </ALink>
                ) : journal?.logo ? (
                  <Image
                    width={100}
                    height={20}
                    src={journal?.logo ?? ''}
                    alt={journal?.label}
                    className={preflightJournalLogo()}
                  />
                ) : (
                  //TODO Apply Hover on title to show full
                  <JournalTitle
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    {truncateTitle(journal?.label || '', 15)}
                    {showTooltip && (journal?.label || '')?.length > 15 ? (
                      <Tooltip>{journal?.label}</Tooltip>
                    ) : (
                      <></>
                    )}
                  </JournalTitle>
                )}
                <Spacer height={8} />
                <div
                  className={css({
                    fontSize: '14px',
                    lineHeight: '18px',
                    fontWeight: '400',
                    color: 'rgba(5, 30, 87, 1)',
                    marginRight: 5,
                    height: '27px',
                    display: 'flex',
                    alignItems: 'center',
                  })()}
                >
                  {t('header.in_collaboration_with', 'in collaboration with')}
                </div>
              </>
            )}
            <Spacer height={8} />
            <Box
              css={{
                position: 'relative',
                height: 'auto',
                width: rem(120),
              }}
            >
              <ALink
                href="https://paperpal.com"
                data-test-id="preflight-logo-link"
                aria-label="Preflight Logo"
              >
                <Image
                  src="/PreflightLogoNew.png"
                  width={400}
                  height={100}
                  alt="Preflight Logo"
                />
              </ALink>
            </Box>
            <Spacer height={24} />
            <Box
              css={{
                position: 'relative',
                height: 'auto',
                width: rem(96),
              }}
            >
              <ALink
                href="https://cactusglobal.com/"
                data-test-id="cactus-logo-link"
                aria-label="Developed by CACTUS"
              >
                <Image src={cactusLogo} alt="Developed by CACTUS" />
              </ALink>
            </Box>
          </Box>
          <Box>
            <Spacer height={32} />
            <FooterP data-test-id="footer-details">
              ©2002-{getYear(new Date())}
              <br />
              Cactus Communications.
              <br />
              All Rights Reserved.
            </FooterP>
          </Box>
        </Column>
        <Column role="row" aria-labelledby="location">
          <FooterH6 id="Location" data-test-id="company-location">
            {t('footer.location')}
          </FooterH6>
          <FooterP>
            Cactus Communications
            <br />
            Services Pte Ltd
            <br />
            20 McCallum Street, #19-01,
            <br />
            Tokio Marine Centre,
            <br />
            Singapore 069046
          </FooterP>
          <Spacer height={32} />
          {journal ? (
            <>
              {' '}
              <FooterH6 data-test-id="preflight-version">
                {t('footer.preflight_version')}
              </FooterH6>
              <FooterP data-test-id="env-version">
                {process.env.NEXT_PUBLIC_VERSION}
              </FooterP>
            </>
          ) : null}
        </Column>
        <Column role="row" aria-labelledby="terms and conditions">
          <FooterH6
            id="terms_and_conditions"
            data-test-id="terms_and_conditions"
          >
            {t('footer.terms_and_conditions')}
          </FooterH6>
          <FooterP>
            <FooterA
              href="https://cactusglobal.com/terms-of-use/"
              target="_blank"
              data-test-id="term-of-use"
              aria-label="Terms of Use"
            >
              {t('footer.terms_of_use')}
            </FooterA>
            <br />
            <FooterA
              href="https://accounts.researcher.life/privacy-policy"
              target="_blank"
              data-test-id="privacy-policy"
              aria-label="Privacy Policy"
            >
              {t('footer.privacy_policy')}
            </FooterA>
            <br />
            <FooterA
              href="https://accounts.researcher.life/privacy-policy"
              target="_blank"
              data-test-id="cookie-policy"
              aria-label="Cookie Policy"
            >
              {t('footer.cookie_policy')}
            </FooterA>
          </FooterP>
          <Spacer height={32} />
          <FooterH6 id="reach_out">{t('footer.reach_out')}</FooterH6>
          <FooterP>
            <FooterA
              data-test-id="reach-out-mail"
              href="mailto:hello@paperpal.com"
              aria-label="Email hello@paperpal.com"
            >
              hello@paperpal.com
            </FooterA>
            <br />
            <FooterA
              href="mailto:preflight-support@paperpal.com"
              aria-label="Email preflight-support@paperpal.com"
            >
              preflight-support@paperpal.com
            </FooterA>
          </FooterP>
        </Column>
        <Column role="row" aria-labelledby="follow us">
          <FooterH6 id="follow_us">{t('footer.follow_us')}</FooterH6>
          <FooterP data-test-id="social-media-handles">
            <FooterA
              href="https://www.facebook.com/PaperpalSolutions"
              aria-label="Facebook"
            >
              Facebook
            </FooterA>
            <br />
            <FooterA
              href="https://twitter.com/teampaperpal"
              aria-label="Twitter"
            >
              Twitter
            </FooterA>
            <br />
            <FooterA
              href="https://www.linkedin.com/showcase/paperpalsolutions/"
              aria-label="LinkedIn"
            >
              LinkedIn
            </FooterA>
          </FooterP>
          {/* <Spacer height={32} />
          {journal ? (
            <FooterH6 data-test-id="admin-journal-login">
              {t('footer.admin_login')}
            </FooterH6>
          ) : null}
          {journal ? (
            <FooterP>
              <FooterA
                href={`/admin/${journal.publisher.slug}/${journal.slug}`}
              >
                {t('footer.login_as')} {journal?.label}{' '}
                {t('footer.login_as_admin')}
              </FooterA>
            </FooterP>
          ) : null} */}
        </Column>
      </Columns>
      <Spacer height={64} />
    </SectionInner>
  );
};

const ALink = withOnLinkClickTracking(styled('a', {}));

const JournalTitle = styled('div', {
  fontSize: '20px',
  fontStyle: 'italic',
  lineHeight: '31px',
  fontWeight: '500',
  display: 'block',
  position: 'relative',
  fontFamily: 'IBM Plex Sans',
  height: '25px',
  color: theme.colors.overlayBlackAlpha12.value,

  // eslint-disable-next-line custom/no-hardcoded-colors
  '&::after': {
    content: 'attr(title)',
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: '#fff',
    padding: '5px 10px',
    borderRadius: '4px',
    fontSize: '14px',
    whiteSpace: 'nowrap',
    display: 'none',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.2s, visibility 0s',
  },

  '&:hover::after': {
    opacity: 1,
    visibility: 'visible',
  },
});

const preflightJournalLogo = css({
  cursor: 'pointer',
  width: 'auto',
  maxHeight: '35px',
});

const Tooltip = styled('div', {
  position: 'absolute',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  color: theme.colors.white,
  padding: '5px 10px',
  borderRadius: '4px',
  fontSize: '14px',
  fontFamily: 'IBM Plex Sans',
  top: '100%',
  left: '30%',
  lineHeight: '22px',
  fontWeight: '400',
  fontStyle: 'normal',
});

function truncateTitle(title: string, length: number) {
  if (title.length > length) {
    const text = title.substring(0, length).trim();
    return `${text}...`;
  } else {
    return title;
  }
}
